import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	TableContainer,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	IconButton,
	Tooltip,
} from '@mui/material';
import { RiArrowUpDownLine } from 'react-icons/ri';
import { sortBy } from 'lodash';

import DeliveryOptionRow from './DeliveryOptionRow';
import { useTranslation } from 'services/localization/useTranslation';
import { DeliveryOptionWithProducts } from 'common/types';
import * as ShopSelectors from 'selectors/ShopSelectors';
import DeliveryOptionsReorderDialog from 'components/DeliveryOptionsReorderDialog';

interface Props {
	deliveryOptions: DeliveryOptionWithProducts[];
}

const DeliveryOptionsTable = ({ deliveryOptions }: Props) => {
	const classes = useStyles();
	const { t } = useTranslation();
	const [isReordering, setIsReordering] = useState<boolean>(false);
	const shopCurrency = useSelector(ShopSelectors.getShopCurrency);
	const sortedDeliveryOptions = sortBy(deliveryOptions, (d) => d.orderIndex);

	return (
		<>
			<TableContainer>
				<Table className={classes.table}>
					<TableHead className={classes.tableHead}>
						<TableRow className={classes.tableRow}>
							<TableCell />
							<TableCell padding="none">{t('delivery.name', 'Name')}</TableCell>
							<TableCell>{t('product.product', 'Product')}</TableCell>
							{/* Add this back after support 2 type of delivery
						<TableCell>{t('delivery.type', 'Type')}</TableCell> */}
							<TableCell align="right">
								{t('delivery.priceCurrency', {
									defaultValue: 'Price ({{currency}})',
									currency: shopCurrency.displayAs,
								})}
							</TableCell>
							<TableCell>{t('delivery.orderLatest', 'Order latest')}</TableCell>
							<TableCell>{t('delivery.available', 'Available')}</TableCell>
							<TableCell>
								<Tooltip title={t('common:actions.changeOrder')}>
									<IconButton onClick={() => setIsReordering(true)} size="large">
										<RiArrowUpDownLine />
									</IconButton>
								</Tooltip>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{sortedDeliveryOptions.map((row) => (
							<DeliveryOptionRow key={row.id} row={row} />
						))}
					</TableBody>
				</Table>
			</TableContainer>
			<DeliveryOptionsReorderDialog
				open={isReordering}
				onClose={() => setIsReordering(false)}
				deliveryOptions={sortedDeliveryOptions}
			/>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		table: {},
		tableHead: {
			backgroundColor: theme.palette.background.secondary,
		},
		tableRow: {
			verticalAlign: 'baseline',
			'& > *': {
				borderBottom: `1px solid ${theme.palette.border.table}`,
				whiteSpace: 'nowrap',
			},
		},
	}),
);

export default DeliveryOptionsTable;
